import React from 'react';
import { Users, Target, Award, Heart } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo, team, stats } from '../mock';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description: 'To empower businesses with cutting-edge digital solutions that drive growth, scalability, and innovation.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We strive for perfection in every line of code and every pixel of design we deliver.'
    },
    {
      icon: Users,
      title: 'Collaboration',
      description: 'Working closely with our clients to ensure their vision becomes reality.'
    },
    {
      icon: Heart,
      title: 'Passion',
      description: 'We love what we do and it shows in the quality of our work and client relationships.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-black mb-6">
                About Codify Tech
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                {companyInfo.description}
              </p>
            </div>
            <div className="relative">
              <div className="relative rounded-lg overflow-hidden shadow-2xl">
                <img
                  src="https://raw.githubusercontent.com/wwwjonnyfaxan786-hash/portfolio-assets/main/About%20Image%20in%20black%20theme.jpg"
                  alt="FlutterFlow App Development"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm md:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="text-center p-6 rounded-lg hover:bg-gray-50 transition-colors duration-300"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-black text-white rounded-full mb-4">
                  <value.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
              Meet Our Core Team
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Talented professionals dedicated to your success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member) => (
              <Card
                key={member.id}
                className="group hover:shadow-xl transition-all duration-300 border-2 border-gray-100 hover:border-black"
              >
                <CardContent className="p-6 text-center">
                  <div className="w-32 h-40 mx-auto mb-6 overflow-hidden rounded-xl bg-gray-200">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-black mb-1">{member.name}</h3>
                  <p className="text-sm text-gray-600 font-medium mb-3">{member.role}</p>

                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
              Our Story
            </h2>
          </div>
          <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
            <p>
              Founded in 2021, Codify Tech began with a simple mission: to make high-quality digital product development accessible to businesses of all sizes. What started as a small team of passionate engineers has grown into a full-stack technology company delivering reliable, scalable, and impactful solutions across industries.
            </p>
            <p>
              Over the years, we’ve successfully completed 150+ projects for 80+ clients, helping startups and enterprises turn ideas into robust mobile apps, web platforms, and cloud-powered systems. Our expertise spans modern frontend frameworks, backend technologies, cloud infrastructure, automation, and intelligent system design.
            </p>
            <p>
              Today, Codify Tech is trusted for building secure, high-performance, and scalable solutions, including custom application development, cloud integrations, advanced backend systems, real-time features, notifications, automation workflows, and API-driven architectures.
            </p>
            <p>
              Our collaborative approach and easy communication make us a trusted partner for businesses looking to build scalable, feature-rich applications.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
